// Welcome 온보딩 — 첫 launch + 익명 사용자 일정 주기 후 노출.
//
// 2026-08 리프레시: 에디토리얼(방향 A). 인테리어 사진이 화면 전체를 채우고
// 아래쪽에만 그라데이션 스크림을 깔아 흰 글자를 얹는다. 예전엔 흰 배경에
// 폰 목업 그림을 놓았는데, 우리가 파는 게 그 방 사진인데 정작 작게 보여주고
// 있었다. 이제 사진이 화면을 끌고 UI 는 뒤로 물러난다.
//
// 사용자가 sign-in 성공하면 onSignIn(provider) 가 App.jsx 의 기존 handleSignIn
// 흐름 그대로 호출 → AuthService 가 익명 → 영구 link → initializeUser 가 크레딧
// 부여. skip 누르면 익명 흐름 그대로 유지 — 강제 가입 아님.
//
// Apple 버튼은 Android 에선 hide (정책 4.8 은 iOS 한정 + Android 의 web
// OAuth flow UX 가 나쁨). SignInModal 과 동일 정책.

import { useLocale, SUPPORTED_LANGS, LANG_LABELS } from '../hooks/useLocale.jsx';
import { isAndroid, isNativeApp } from '../services/platform-service.js';
import { useCreditConfig } from '../services/credit-config.js';
import { HERO_IMAGE } from '../services/hero-image.js';

export function WelcomeScreen({ onClose, onSignIn, loading, error }) {
  const { t, lang, setLang } = useLocale();
  // 가입 첫날에 실제로 손에 쥐는 개수 = signup bonus 뿐. 가입 당일은 daily
  // 미지급 (서버 initCredits 가 첫날 daily 를 소진 처리 — functions/credits.js).
  const firstDayCredits = useCreditConfig().signupBonus;

  return (
    <div className="welcome-screen" role="dialog" aria-modal="true">
      {/* 배경 사진 + 스크림. aria-hidden — 장식이라 스크린리더가 읽을 게 없다. */}
      <img className="welcome-bg" src={HERO_IMAGE} alt="" aria-hidden="true" />
      <div className="welcome-scrim" aria-hidden="true" />

      <div className="welcome-inner">
        <header className="welcome-header">
          <span className="welcome-eyebrow">archelier</span>
          <select
            className="welcome-lang"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            aria-label="Language"
          >
            {SUPPORTED_LANGS.map((l) => (
              <option key={l} value={l}>{LANG_LABELS[l]}</option>
            ))}
          </select>
        </header>

        {/* 사진이 보이도록 가운데는 비운다 — 카피와 버튼은 아래로 몰아 놓는다. */}
        <div className="welcome-main">
          <h1 className="welcome-headline">{t('landingHeadline')}</h1>
          <p className="welcome-desc">{t('proTrialIntroDesc', { n: firstDayCredits })}</p>

          {error === 'popupBlocked' && (
            <p className="welcome-error">{t('signInPopupBlocked')}</p>
          )}

          <div className="welcome-actions">
            <button
              className="btn btn-block signin-google-btn"
              onClick={() => onSignIn('google')}
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
                <path d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.826.957 4.039l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              {t('signInGoogle')}
            </button>

            {!isAndroid() && (
              <button
                className="btn btn-block signin-apple-btn"
                onClick={() => onSignIn('apple')}
                disabled={loading}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.94 9.42c-.02-2.08 1.7-3.08 1.78-3.13-.97-1.42-2.49-1.61-3.03-1.63-1.29-.13-2.51.76-3.16.76-.66 0-1.66-.74-2.73-.72-1.4.02-2.7.82-3.42 2.07C2.94 9.31 4.04 13 5.45 14.99c.69.97 1.5 2.06 2.57 2.02 1.04-.04 1.43-.67 2.69-.67 1.25 0 1.61.67 2.71.65 1.12-.02 1.83-.99 2.51-1.97.79-1.13 1.12-2.23 1.13-2.29-.03-.01-2.16-.83-2.18-3.31zM12.85 3.4c.57-.7.96-1.66.85-2.62-.83.03-1.83.55-2.42 1.24-.53.61-.99 1.6-.86 2.54.92.07 1.86-.47 2.43-1.16z" fill="currentColor"/>
                </svg>
                {t('signInApple')}
              </button>
            )}
          </div>

          {/* "계정 없이 계속" 은 **네이티브 앱에서만**. 웹에서 게스트로 열어
              주면 앱 게이트를 세워 놓고 옆문으로 2 크레딧을 쥐여 주는 셈이다.
              웹에서 할 수 있는 건 로그인하거나 앱을 받는 것, 둘뿐이다.

              닫을 방법이 사라지므로 **보러 온 사람 앞에는 이 화면 자체를
              띄우지 않는다** — App.jsx 의 `VIEW_ONLY_PATTERNS` 가 디자인·공유·
              프로필·컬렉션 경로에서 Welcome 을 막는다. 둘은 한 쌍이라 한쪽만
              고치면 안 된다. */}
          {isNativeApp() && (
            <button
              type="button"
              className="welcome-skip"
              onClick={onClose}
              disabled={loading}
            >
              {t('welcomeSkip')}
            </button>
          )}

          <p className="welcome-legal">
            <a href="/terms" target="_blank" rel="noopener noreferrer">{t('terms')}</a>
            {' · '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">{t('privacy')}</a>
          </p>
        </div>
      </div>
    </div>
  );
}
